import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  AgentKpiSummary,
  LeaderboardResponse,
  PersistencyTrendsResponse,
  PromotionProgressDto,
  PayrollReconciliation,
  PaymentTimelinessSummary,
  TeamStructureResponse,
  SalesTarget,
  SalesTargetResponse,
  BadgesResponse,
  StreakInfo,
  DailyChallengeResponse,
  NotificationsResponse,
} from '../models/agent-hub.models';

@Injectable({
  providedIn: 'root',
})
export class AgentHubApiService {
  private baseUrl = `${environment.apiUrl}/agent-hub`;

  constructor(private http: HttpClient) {}

  // ── KPI ─────────────────────────────────────────────────────────────

  getMyKpi(year: number, month: number): Observable<AgentKpiSummary> {
    const params = new HttpParams().set('year', year).set('month', month);
    return this.http.get<AgentKpiSummary>(`${this.baseUrl}/my-kpi`, { params });
  }

  // ── Payslip ─────────────────────────────────────────────────────────

  getMyPayslip(year: number, month: number): Observable<PayrollReconciliation> {
    const params = new HttpParams().set('year', year).set('month', month);
    return this.http.get<PayrollReconciliation>(`${this.baseUrl}/my-payslip`, { params });
  }

  getMyPayslips(year?: number): Observable<PayrollReconciliation[]> {
    let params = new HttpParams();
    if (year) params = params.set('year', year);
    return this.http.get<PayrollReconciliation[]>(`${this.baseUrl}/my-payslips`, { params });
  }

  // ── Persistency Trends ──────────────────────────────────────────────

  getMyPersistencyTrends(months: number = 12): Observable<PersistencyTrendsResponse> {
    const params = new HttpParams().set('months', months);
    return this.http.get<PersistencyTrendsResponse>(`${this.baseUrl}/my-persistency-trends`, {
      params,
    });
  }

  // ── Promotion Progress ──────────────────────────────────────────────

  getMyPromotionProgress(): Observable<PromotionProgressDto> {
    return this.http.get<PromotionProgressDto>(`${this.baseUrl}/my-promotion-progress`);
  }

  // ── Team Leaderboard (UM/SEM only) ──────────────────────────────────

  getTeamLeaderboard(
    year: number,
    month: number,
    rankBy: string = 'FYC',
    limit: number = 20,
    position?: string
  ): Observable<LeaderboardResponse> {
    let params = new HttpParams()
      .set('year', year)
      .set('month', month)
      .set('rankBy', rankBy)
      .set('limit', limit);
    if (position) {
      params = params.set('position', position);
    }
    return this.http.get<LeaderboardResponse>(`${this.baseUrl}/team-leaderboard`, { params });
  }

  // ── Payment Timeliness ──────────────────────────────────────────

  getPaymentTimeliness(year: number, month: number): Observable<PaymentTimelinessSummary> {
    const params = new HttpParams().set('year', year).set('month', month);
    return this.http.get<PaymentTimelinessSummary>(`${this.baseUrl}/payment-timeliness`, { params });
  }

  // ── Team Structure ──────────────────────────────────────────────

  getMyTeamStructure(): Observable<TeamStructureResponse> {
    return this.http.get<TeamStructureResponse>(`${this.baseUrl}/my-team-structure`);
  }

  // ── Sales Targets (SEM) ─────────────────────────────────────────

  getSalesTargets(year: number): Observable<SalesTargetResponse> {
    const params = new HttpParams().set('year', year);
    return this.http.get<SalesTargetResponse>(`${this.baseUrl}/sales-targets`, { params });
  }

  saveSalesTarget(target: SalesTarget): Observable<SalesTarget> {
    return this.http.post<SalesTarget>(`${this.baseUrl}/sales-targets`, target);
  }

  deleteSalesTarget(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/sales-targets/${id}`);
  }

  // ── Gamification — Phase 7.2 ───────────────────────────────────────────

  getMyBadges(): Observable<BadgesResponse> {
    return this.http.get<BadgesResponse>(`${this.baseUrl}/my-badges`);
  }

  getMyStreak(): Observable<StreakInfo> {
    return this.http.get<StreakInfo>(`${this.baseUrl}/my-streak`);
  }

  getDailyChallenge(): Observable<DailyChallengeResponse> {
    return this.http.get<DailyChallengeResponse>(`${this.baseUrl}/daily-challenge`);
  }

  // ── Notifications ─────────────────────────────────────────────────────

  getMyNotifications(skip = 0, take = 20): Observable<NotificationsResponse> {
    const params = new HttpParams().set('skip', skip).set('take', take);
    return this.http.get<NotificationsResponse>(`${environment.apiUrl}/noti/user`, { params });
  }

  markNotificationRead(id: number): Observable<unknown> {
    return this.http.post(`${environment.apiUrl}/noti/${id}/mark-read`, {});
  }

  markAllNotificationsRead(): Observable<unknown> {
    return this.http.post(`${environment.apiUrl}/noti/mark-all-read`, {});
  }
}
